import React, { useState, useEffect, useCallback } from 'react';
import { printHtmlSilently } from '../lib/silentPrint';
import axios from 'axios';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { Calendar, DollarSign, Users, TrendingUp, Download, ArrowDownRight, ArrowUpRight, Filter, Printer } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METHOD_LABELS = {
  dinheiro: 'Dinheiro',
  pix: 'PIX Loja',
  pix_maquininha: 'PIX Maquininha',
  credito: 'Crédito',
  cartao: 'Cartão',
  debito: 'Débito',
  voucher: 'Voucher',
  cheque: 'Cheque',
  boleto: 'Boleto',
  transferencia: 'Transferência',
  outros: 'Outros',
};

const METHOD_OPTIONS = [
  'dinheiro', 'pix', 'pix_maquininha', 'credito', 'debito', 'voucher', 'cheque', 'boleto', 'transferencia'
];

const PERIODS = [
  { value: 7, label: '7 dias' },
  { value: 30, label: '30 dias' },
  { value: 90, label: '90 dias' },
  { value: 180, label: '6 meses' },
  { value: 365, label: '1 ano' },
];

export const ReceivablesReport = () => {
  const [days, setDays] = useState(30);
  const [filterMethod, setFilterMethod] = useState('all');
  const [filterOperator, setFilterOperator] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days: String(days), compare_previous: 'true' });
      if (filterMethod !== 'all') params.append('payment_method', filterMethod);
      if (filterOperator !== 'all') params.append('operator', filterOperator);
      const r = await axios.get(`${API}/accounts/receivables/received/report?${params.toString()}`, { withCredentials: true });
      setData(r.data);
    } catch (err) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [days, filterMethod, filterOperator]);

  useEffect(() => { load(); }, [load]);

  const exportCSV = () => {
    if (!data) return;
    const rows = [
      ['Relatório de Recebimentos'],
      [`Período: últimos ${data.period_days} dias`],
      [`Total: R$ ${data.total_received.toFixed(2)} em ${data.total_count} pagamento(s)`],
      [],
      ['Data', 'Cliente', 'Venda', 'Forma', 'Valor (R$)']
    ];
    data.recent.forEach(r => {
      rows.push([
        r.received_at ? new Date(r.received_at).toLocaleString('pt-BR') : '—',
        r.customer_name || '',
        r.sale_number ? `#${r.sale_number}` : '',
        METHOD_LABELS[r.payment_method] || r.payment_method || '',
        Number(r.amount).toFixed(2)
      ]);
    });
    const csv = rows.map(row => row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `recebimentos-${days}d-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    if (!data) return;

    const methodRows = (data.by_method || []).map(m =>
      `<tr><td>${METHOD_LABELS[m.method] || m.method}</td><td class="right">R$ ${Number(m.total).toFixed(2)}</td><td class="right">${m.percent}%</td></tr>`
    ).join('') || `<tr><td colspan="3" class="center small">Sem dados</td></tr>`;

    const topCustRows = (data.top_customers || []).slice(0, 10).map((c, i) =>
      `<tr><td>${i + 1}. ${(c.name || '').substring(0, 22)}</td><td class="right">${c.count}x</td><td class="right">R$ ${Number(c.total).toFixed(2)}</td></tr>`
    ).join('') || `<tr><td colspan="3" class="center small">Sem clientes</td></tr>`;

    const recentRows = (data.recent || []).slice(0, 30).map(r =>
      `<tr><td class="small">${r.received_at ? new Date(r.received_at).toLocaleDateString('pt-BR') : '-'}</td><td class="small">${(r.customer_name || '').substring(0, 14)}</td><td class="right small">R$ ${Number(r.amount).toFixed(2)}</td></tr>`
    ).join('') || `<tr><td colspan="3" class="center small">Sem pagamentos</td></tr>`;

    const html = `
      <div class="center bold" style="font-size:13px">RECEBIMENTOS</div>
      <div class="center small">Ultimos ${data.period_days} dias</div>
      <div class="center small">Emitido: ${new Date().toLocaleString('pt-BR')}</div>
      <hr/>
      <table><tbody>
        <tr><td>Total recebido:</td><td class="right bold">R$ ${Number(data.total_received).toFixed(2)}</td></tr>
        <tr><td>Pagamentos:</td><td class="right">${data.total_count}</td></tr>
        <tr><td>Ticket medio:</td><td class="right">R$ ${data.total_count > 0 ? (data.total_received / data.total_count).toFixed(2) : '0.00'}</td></tr>
      </tbody></table>
      <hr/>
      <div class="bold">POR FORMA DE PAGAMENTO</div>
      <table><tbody>${methodRows}</tbody></table>
      <hr/>
      <div class="bold">TOP CLIENTES</div>
      <table><tbody>${topCustRows}</tbody></table>
      <hr/>
      <div class="bold">ULTIMOS PAGAMENTOS</div>
      <table><thead>
        <tr><th class="left small">Data</th><th class="left small">Cliente</th><th class="right small">Valor</th></tr>
      </thead><tbody>${recentRows}</tbody></table>
      <hr/>
      <div class="center small">Smart Flow</div>
    `;
    printHtmlSilently(`
      <html><head><title>Recebimentos</title>
      <style>
        @page { margin: 0; size: 80mm auto; }
        body { font-family: 'Courier New', monospace; font-size: 11px; padding: 8px; color: #000; margin: 0; }
        hr { border: none; border-top: 1px dashed #000; margin: 6px 0; }
        table { width: 100%; border-collapse: collapse; }
        td, th { padding: 2px 0; vertical-align: top; }
        .right { text-align: right; } .center { text-align: center; } .left { text-align: left; }
        .bold { font-weight: bold; } .small { font-size: 10px; }
      </style>
      </head><body>${html}</body></html>
    `);
  };

  const prev = data?.previous_period;
  const delta = prev?.delta ?? 0;
  const isUp = delta >= 0;

  return (
    <div data-testid="receivables-report">
      {/* Filtros + Período + Ações */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4 print:hidden">
        <div className="flex items-center gap-2 flex-wrap">
          <Calendar size={16} className="text-slate-500" />
          <span className="text-sm font-medium text-slate-700">Período:</span>
          {PERIODS.map(p => (
            <Button
              key={p.value}
              size="sm"
              variant={days === p.value ? 'default' : 'outline'}
              onClick={() => setDays(p.value)}
              className={days === p.value ? 'bg-emerald-600 hover:bg-emerald-700 h-8' : 'h-8'}
              data-testid={`report-period-${p.value}`}
            >
              {p.label}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={exportCSV} className="h-8" data-testid="export-csv-btn">
            <Download size={14} className="mr-1" /> Excel/CSV
          </Button>
          <Button size="sm" variant="outline" onClick={handlePrint} className="h-8" data-testid="print-report-btn">
            <Printer size={14} className="mr-1" /> Imprimir/PDF
          </Button>
        </div>
      </div>

      {/* Filtros avançados */}
      <div className="flex items-center gap-3 mb-4 flex-wrap print:hidden">
        <Filter size={14} className="text-slate-500" />
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-slate-600">Forma:</span>
          <Select value={filterMethod} onValueChange={setFilterMethod}>
            <SelectTrigger className="h-8 w-44" data-testid="report-filter-method">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as formas</SelectItem>
              {METHOD_OPTIONS.map(m => <SelectItem key={m} value={m}>{METHOD_LABELS[m]}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-slate-600">Operador:</span>
          <Select value={filterOperator} onValueChange={setFilterOperator}>
            <SelectTrigger className="h-8 w-48" data-testid="report-filter-operator">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os operadores</SelectItem>
              {(data?.operators || []).map(op => (
                <SelectItem key={op.name} value={op.name}>{op.name} (R$ {op.total.toFixed(2)})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {(filterMethod !== 'all' || filterOperator !== 'all') && (
          <Button size="sm" variant="ghost" onClick={() => { setFilterMethod('all'); setFilterOperator('all'); }} className="h-8 text-xs text-slate-500">
            Limpar filtros
          </Button>
        )}
      </div>

      {loading ? (
        <p className="text-center p-8 text-slate-500">Carregando relatório...</p>
      ) : !data ? (
        <p className="text-center p-8 text-slate-500">Sem dados disponíveis.</p>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wider font-bold text-emerald-700">Total recebido</p>
                <DollarSign size={16} className="text-emerald-600" />
              </div>
              <p className="text-2xl font-black text-emerald-700" data-testid="report-total">R$ {data.total_received.toFixed(2)}</p>
              <p className="text-[10px] text-emerald-600">{data.total_count} pagamento(s)</p>
            </div>
            {prev && (
              <div className={`border rounded-md p-3 ${isUp ? 'bg-blue-50 border-blue-200' : 'bg-orange-50 border-orange-200'}`} data-testid="report-comparison">
                <div className="flex items-center justify-between">
                  <p className={`text-xs uppercase tracking-wider font-bold ${isUp ? 'text-blue-700' : 'text-orange-700'}`}>vs período anterior</p>
                  {isUp ? <ArrowUpRight size={16} className="text-blue-600" /> : <ArrowDownRight size={16} className="text-orange-600" />}
                </div>
                <p className={`text-2xl font-black ${isUp ? 'text-blue-700' : 'text-orange-700'}`}>
                  {isUp ? '+' : ''}R$ {delta.toFixed(2)}
                </p>
                <p className={`text-[10px] ${isUp ? 'text-blue-600' : 'text-orange-600'}`}>
                  {isUp ? '+' : ''}{prev.delta_percent.toFixed(1)}% · anterior R$ {prev.total_received.toFixed(2)}
                </p>
              </div>
            )}
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wider font-bold text-amber-700">Ticket médio</p>
                <TrendingUp size={16} className="text-amber-600" />
              </div>
              <p className="text-2xl font-black text-amber-700">
                R$ {data.total_count > 0 ? (data.total_received / data.total_count).toFixed(2) : '0.00'}
              </p>
              <p className="text-[10px] text-amber-600">por pagamento</p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wider font-bold text-slate-700">Clientes ativos</p>
                <Users size={16} className="text-slate-600" />
              </div>
              <p className="text-2xl font-black text-slate-700">{data.top_customers.length}</p>
              <p className="text-[10px] text-slate-600">com pagamentos no período</p>
            </div>
          </div>

          {/* Gráfico de evolução diária */}
          {data.daily.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-md p-3 mb-4" data-testid="report-daily-chart">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">Recebimentos por dia</p>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={data.daily} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10 }}
                    tickFormatter={(d) => {
                      const [, m, day] = d.split('-');
                      return `${day}/${m}`;
                    }}
                  />
                  <YAxis tick={{ fontSize: 10 }} width={50} tickFormatter={(v) => `R$${v}`} />
                  <Tooltip
                    formatter={(v) => `R$ ${Number(v).toFixed(2)}`}
                    labelFormatter={(d) => new Date(d).toLocaleDateString('pt-BR')}
                    contentStyle={{ fontSize: 11 }}
                  />
                  <Bar dataKey="total" fill="#059669" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Grid: Breakdown método + Ranking clientes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            {/* Breakdown por forma de pagamento */}
            <div className="bg-white border border-slate-200 rounded-md p-3" data-testid="report-by-method">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-3">Por forma de pagamento</p>
              {data.by_method.length === 0 ? (
                <p className="text-xs text-slate-500 text-center p-4">Sem pagamentos</p>
              ) : (
                <div className="space-y-2">
                  {data.by_method.map((m) => (
                    <div key={m.method} className="flex items-center gap-2" data-testid={`method-row-${m.method}`}>
                      <div className="w-28 text-xs font-medium text-slate-700">{METHOD_LABELS[m.method] || m.method}</div>
                      <div className="flex-1 bg-slate-100 rounded-full h-5 relative overflow-hidden">
                        <div
                          className="bg-emerald-500 h-full transition-all"
                          style={{ width: `${m.percent}%` }}
                        />
                        <span className="absolute inset-0 text-[10px] flex items-center justify-end px-2 font-bold text-slate-700">
                          R$ {m.total.toFixed(2)} ({m.percent}%)
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Top clientes */}
            <div className="bg-white border border-slate-200 rounded-md p-3" data-testid="report-top-customers">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-3">Top clientes (pagamentos)</p>
              {data.top_customers.length === 0 ? (
                <p className="text-xs text-slate-500 text-center p-4">Sem pagamentos</p>
              ) : (
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {data.top_customers.map((c, i) => (
                    <div key={c.name} className="flex items-center justify-between text-sm py-1 border-b border-slate-100">
                      <div className="flex items-center gap-2">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                          i === 0 ? 'bg-yellow-100 text-yellow-700' :
                          i === 1 ? 'bg-slate-200 text-slate-700' :
                          i === 2 ? 'bg-orange-100 text-orange-700' :
                          'bg-slate-100 text-slate-500'
                        }`}>{i + 1}</span>
                        <span className="font-medium">{c.name}</span>
                        <span className="text-[10px] text-slate-500">({c.count}x)</span>
                      </div>
                      <span className="font-bold text-emerald-700">R$ {c.total.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Lista de recebimentos recentes */}
          <div className="bg-white border border-slate-200 rounded-md p-3" data-testid="report-recent">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-3">Últimos pagamentos ({data.recent.length})</p>
            {data.recent.length === 0 ? (
              <p className="text-xs text-slate-500 text-center p-4">Nenhum pagamento no período</p>
            ) : (
              <div className="overflow-x-auto max-h-60 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-slate-50">
                    <tr className="text-xs uppercase tracking-wider text-slate-600">
                      <th className="text-left p-2">Data</th>
                      <th className="text-left p-2">Cliente</th>
                      <th className="text-left p-2">Venda</th>
                      <th className="text-left p-2">Forma</th>
                      <th className="text-right p-2">Valor</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {data.recent.map((r) => (
                      <tr key={r.id}>
                        <td className="p-2 text-xs">{r.received_at ? new Date(r.received_at).toLocaleDateString('pt-BR') : '—'}</td>
                        <td className="p-2 font-medium">{r.customer_name}</td>
                        <td className="p-2 text-blue-700 font-bold">{r.sale_number ? `#${r.sale_number}` : '—'}</td>
                        <td className="p-2 text-xs capitalize">{METHOD_LABELS[r.payment_method] || r.payment_method || '—'}</td>
                        <td className="p-2 text-right font-bold text-emerald-700">R$ {Number(r.amount).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ReceivablesReport;
